import React, { useEffect, useRef, useState } from "react";
import { ChatMessage } from "./types";
import { Button } from "@/components/ui/button";
// import { processMessageContent } from "@/utils/followUpSuggestions";
import ChatMessageBubble from "./ChatMessageBubble";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Tables } from "@/types/database.types";
import { TypingIndicator } from "@/components/ui/TypingIndicator";

type ChatMessagesProps = {
  chatMessages: ChatMessage[];
  isLoading: boolean;
  agent: Tables<"agents">;
  organization?: Tables<"organizations">;
  brandColor?: string;
  textColor?: string;
  isPublic?: boolean;
  isMobile?: boolean;
  onSendMessage: (message: string) => void;
  onCloseConversation?: () => void;
  hasMoreMessages?: boolean;
  onLoadMoreMessages?: () => Promise<void>;
  isLoadingMore?: boolean;
  isConversationLoading?: boolean;
};

// type ProcessedChatMessage = ChatMessage & { displayContent?: string };

const ChatMessages: React.FC<ChatMessagesProps> = ({
  chatMessages,
  isLoading,
  agent,
  organization,
  brandColor = "#060520",
  textColor = "#FFFFFF",
  // isPublic = false,
  // isMobile = false,
  onSendMessage,
  onCloseConversation,
  // hasMoreMessages = false,
  // onLoadMoreMessages,
  isLoadingMore = false,
  isConversationLoading = false,
}) => {
  /* ------------------------------------------------------------------ */
  /* scroll‑handling                                                     */
  /* ------------------------------------------------------------------ */
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // NEW: Track how many messages to display
  const MESSAGES_PER_PAGE = 20;
  const [messagesToShow, setMessagesToShow] = useState(MESSAGES_PER_PAGE);

  // When chatMessages changes (e.g., new conversation), reset messagesToShow
  useEffect(() => {
    setMessagesToShow(MESSAGES_PER_PAGE);
  }, [chatMessages]);

  // track manual scroll position
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const atBottom =
        Math.abs(
          container.scrollHeight - container.scrollTop - container.clientHeight,
        ) < 50;
      setAutoScroll(atBottom);
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, []);

  // actual scroll
  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (autoScroll) scrollToBottom();
  }, [chatMessages, isLoading, autoScroll]);

  /* ------------------------------------------------------------------ */
  /* message preprocessing                                               */
  /* ------------------------------------------------------------------ */
  // Only show the last N messages
  const totalMessages = chatMessages.length;
  const startIdx = Math.max(0, totalMessages - messagesToShow);
  const visibleMessages = chatMessages.slice(startIdx);

  // Apply 1.25rem padding (p-5) for all views
  const padding = "p-5";

  /* ------------------------------------------------------------------ */
  /* render                                                              */
  /* ------------------------------------------------------------------ */
  return (
    <div className="w-full h-full relative">
      {/* scroll container */}
      <div
        ref={chatContainerRef}
        className={`flex flex-col ${padding} space-y-4 overflow-y-auto h-full`}
      >
        {isConversationLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <div className="flex flex-col items-center">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: brandColor }}
              ></div>
              <div className="text-sm mt-2 text-gray-500">
                Loading conversation...
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Load More button (pagination) */}
            {totalMessages > messagesToShow && (
              <div className="flex justify-center sticky top-0 z-10 py-2">
                <Button
                  onClick={() =>
                    setMessagesToShow((prev) => prev + MESSAGES_PER_PAGE)
                  }
                  disabled={isLoadingMore}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                >
                  <ChevronUp size={16} />
                  <span>Load earlier messages</span>
                </Button>
              </div>
            )}

            {/* empty‑state */}
            {visibleMessages.length === 0 && !isLoading ? (
              <div className="flex items-center justify-center py-10">
                <p className="text-muted-foreground">
                  Start a conversation with{" "}
                  {agent?.agent_name || agent?.title || "the AI"}
                </p>
              </div>
            ) : (
              visibleMessages.map((msg, i) => (
                <ChatMessageBubble
                  key={startIdx + i}
                  content={msg.content}
                  type={msg.type}
                  agent={agent}
                  organization={organization}
                  isLoading={
                    isLoading &&
                    startIdx + i === chatMessages.length - 1 &&
                    msg.type === "agent"
                  }
                  brandColor={brandColor}
                  textColor={textColor}
                  onCloseConversation={onCloseConversation}
                  onSendMessage={onSendMessage}
                />
              ))
            )}

            {/* typing dots */}
            {isLoading && <TypingIndicator />}

            {/* anchor for scroll‑to‑bottom */}
            <div ref={messagesEndRef} className="h-1" />
          </>
        )}
      </div>

      {/* floating “scroll to latest” button */}
      {!autoScroll && visibleMessages.length > 0 && (
        <Button
          onClick={() => {
            scrollToBottom();
            setAutoScroll(true);
          }}
          className="fixed bottom-24 right-4 h-10 w-10 p-2 rounded-full shadow-md z-20"
          style={{ backgroundColor: brandColor, color: textColor }}
          aria-label="Scroll to latest messages"
        >
          <ChevronDown size={20} />
        </Button>
      )}
    </div>
  );
};

export { ChatMessages };
